import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { employeeAPI } from '../../services/api';
import { toast } from 'sonner';
import { TrendingUp, Award, Calendar, Star, BarChart3 } from 'lucide-react';

const EmployeeAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await employeeAPI.getAnalytics();
      setAnalytics(res.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B5AFF]"></div>
        </div>
      </DashboardLayout>
    );
  }

  const maxRating = 5;
  const avgRating = analytics?.rating_history?.length
    ? (analytics.rating_history.reduce((sum, r) => sum + r.rating, 0) / analytics.rating_history.length).toFixed(1)
    : null;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0F1E]">My Performance</h1>
          <p className="text-[#5B6472]">Track your professional growth over time</p>
        </div>

        {/* Summary Cards */}
        {avgRating && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                  <Star className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <p className="text-[#5B6472] text-sm">Average Rating</p>
                  <p className="text-2xl font-bold text-[#0A0F1E]">{avgRating} / {maxRating}</p>
                </div>
              </div>
            </Card>

            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-[#5B6472] text-sm">Total Reviews</p>
                  <p className="text-2xl font-bold text-[#0A0F1E]">{analytics?.rating_history?.length || 0}</p>
                </div>
              </div>
            </Card>

            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <Award className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-[#5B6472] text-sm">Top Skill</p>
                  <p className="text-lg font-bold text-[#0A0F1E] truncate">
                    {analytics?.top_skills?.[0]?.skill || '-'}
                  </p>
                </div>
              </div>
            </Card>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Rating History Chart */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp className="w-5 h-5 text-[#1B5AFF]" />
              <h2 className="text-lg font-semibold text-[#0A0F1E]">Rating History</h2>
            </div>
            
            {analytics?.rating_history?.length > 0 ? (
              <div className="space-y-4">
                {/* Line chart visualization */}
                <div className="h-48 flex items-end gap-2 border-b border-l border-[#E8EDF5] pl-6 pb-6 relative">
                  {/* Y-axis labels */}
                  <div className="absolute left-0 top-0 bottom-6 flex flex-col justify-between text-xs text-[#5B6472]">
                    <span>5</span>
                    <span>4</span>
                    <span>3</span>
                    <span>2</span>
                    <span>1</span>
                  </div>
                  
                  {/* Data points */}
                  {analytics.rating_history.slice(-10).map((item, index) => (
                    <div key={index} className="flex-1 flex flex-col items-center gap-1 h-full justify-end">
                      <div 
                        className="w-3 h-3 rounded-full bg-[#1B5AFF] shadow-lg shadow-[#1B5AFF]/50"
                        style={{ marginBottom: `${((item.rating - 1) / 4) * 100}%` }}
                        title={`${item.date}: ${item.rating}/5`}
                      />
                    </div>
                  ))}
                </div>
                
                {/* X-axis labels */}
                <div className="flex justify-between text-xs text-[#5B6472] pl-6">
                  {analytics.rating_history.slice(-10).map((item, index) => (
                    <span key={index} className="flex-1 text-center truncate">
                      {item.date.slice(5)}
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center">
                <p className="text-[#5B6472]">No rating history yet</p>
              </div>
            )}
          </Card>

          {/* Top Skills */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <div className="flex items-center gap-2 mb-6">
              <Award className="w-5 h-5 text-[#1B5AFF]" />
              <h2 className="text-lg font-semibold text-[#0A0F1E]">My Recognized Strengths</h2>
            </div>
            
            {analytics?.top_skills?.length > 0 ? (
              <div className="space-y-3">
                {analytics.top_skills.map((skill, index) => {
                  const maxCount = analytics.top_skills[0]?.count || 1;
                  const percentage = (skill.count / maxCount) * 100;
                  return (
                    <div key={index} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-[#0A0F1E]">{skill.skill}</span>
                        <span className="text-[#5B6472]">{skill.count}x mentioned</span>
                      </div>
                      <div className="h-2 bg-[#F5F8FF] rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-green-500 to-green-500/50 rounded-full"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center">
                <p className="text-[#5B6472]">No skills data yet</p>
              </div>
            )}
          </Card>
        </div>

        {/* Review Timeline */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <div className="flex items-center gap-2 mb-6">
            <Calendar className="w-5 h-5 text-[#1B5AFF]" />
            <h2 className="text-lg font-semibold text-[#0A0F1E]">Review Timeline</h2>
          </div>
          
          {analytics?.review_timeline?.length > 0 ? (
            <div className="space-y-4">
              {analytics.review_timeline.map((review, index) => (
                <Link 
                  key={index}
                  to={`/employee/reviews/${review.id}`}
                  className="flex items-center gap-4 p-4 bg-[#F5F8FF]/30 rounded-xl hover:bg-[#F5F8FF]/50 transition-colors"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#F5F8FF] flex items-center justify-center">
                    <span className="text-lg font-bold text-[#1B5AFF]">{review.rating || '-'}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[#0A0F1E] font-medium truncate">{review.company_name}</p>
                    <p className="text-[#5B6472] text-sm">{review.period}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[#5B6472] text-sm">{review.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center">
              <p className="text-[#5B6472]">No reviews yet</p>
            </div>
          )}
        </Card>

        {/* Empty State */}
        {!analytics?.rating_history?.length && !analytics?.top_skills?.length && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-12 text-center">
            <BarChart3 className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[#0A0F1E] mb-2">No Performance Data Yet</h3>
            <p className="text-[#5B6472] max-w-md mx-auto">
              Once you receive performance reviews from employers, you'll see your rating history, recognized strengths, and growth trends here.
            </p>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default EmployeeAnalytics;
